<template>
  <div class="flex m-8 justify-start flex-wrap">
    <div class="md:max-w-xs w-full mb-4 md:mb-0">
      <p class="pb-2 text-gray-500 font-medium">Dimensiones de tu proyecto</p>
      <p class="text-sm text-gray-400 mb-4">
        Llena la información sobre las dimensiones del proyecto. Aquí deberás
        ingresar únicamente los metros cuadrados de las paredes exteriores de la
        construcción
      </p>
      <div class="w-full">
        <div class="flex">
          <label class="block text-sm font-medium leading-5 text-gray-700"
            >Rotar dimensiones</label
          >
        </div>

        <button
          type="button"
          @click="$refs.rotateClockwiseModal.open()"
          class="mt-1 inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-gray-800 hover:bg-text-600 focus:outline-none focus:shadow-outline-indigo focus:border-indigo-600 transition duration-150 ease-in-out"
        >
          Segun las manecillas
        </button>
        <button
          type="button"
          @click="$refs.rotateCounterClockwiseModal.open()"
          class="mt-1 inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-gray-800 hover:bg-text-600 focus:outline-none focus:shadow-outline-indigo focus:border-indigo-600 transition duration-150 ease-in-out"
        >
          Encontra de las manecillas
        </button>
      </div>
    </div>

    <div class="flex-1 md:ml-5">
      <div class="flex w-full flex-wrap">
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Tipo de proyecto</label
            >
            <div
              v-if="$v.projectDimensions.project_type.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.project_type.$model"
            :options="projectTypeOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Orientación de fachada principal</label
            >
            <div
              v-if="$v.projectDimensions.orientation_front.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.orientation_front.$model"
            :options="orientationOptions"
          ></select-buttons>
        </div>

        <!-- Roof -->
        <div class="w-full border-t border-gray-200 my-6"></div>
        <div class="w-full mb-4">
          <p class="text-gray-500 font-medium pb-2">
            Azotea
          </p>
          <p class="text-gray-400 text-sm">
            Ingresa aquí los metros cuadrados de vidrio y sólido (pared).
          </p>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio</label
            >
            <div
              v-if="$v.projectDimensions.roof_has_glass.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.roof_has_glass.$model"
            :options="glassOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="roof_solid_m2"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Area de azotea (m²)</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="roof_solid_m2"
              v-model.trim="$v.projectDimensions.roof_solid_m2.$model"
              type="number"
              step="any"
              required
              class="form-input block w-full sm:text-sm sm:leading-5"
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectDimensions.roof_solid_m2.$error,
              }"
            />
            <div
              v-if="$v.projectDimensions.roof_solid_m2.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div class="w-full lg:w-1/2 mb-4 lg:px-2" v-if="projectDimensions.roof_has_glass">
            <label
              for="roof_glass_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="roof_glass_m2"
                v-model.trim="$v.projectDimensions.roof_glass_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.roof_glass_m2.$error,  
                }"
              />
              <div
                v-if="$v.projectDimensions.roof_glass_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <!-- End Roof -->

        <!-- North wall -->
        <div class="w-full border-t border-gray-200 my-6"></div>
        <div class="w-full mb-4">
          <p class="text-gray-500 font-medium pb-2">
            Muro norte
          </p>
          <p class="text-gray-400 text-sm">
            Ingresa aquí la información de los metros cuadrados del muro norte.
          </p>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio</label
            >
            <div
              v-if="$v.projectDimensions.north_wall_has_glass.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.north_wall_has_glass.$model"
            :options="glassOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Puertas</label
            >
            <div
              v-if="$v.projectDimensions.north_wall_has_doors.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.north_wall_has_doors.$model"
            :options="doorsOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="north_wall_solid_m2"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Area de muro (m²)</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="north_wall_solid_m2"
              v-model.trim="$v.projectDimensions.north_wall_solid_m2.$model"
              type="number"
              step="any"
              required
              class="form-input block w-full sm:text-sm sm:leading-5"
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectDimensions.north_wall_solid_m2.$error,
              }"
            />
            <div
              v-if="$v.projectDimensions.north_wall_solid_m2.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="w-full lg:w-1/2 mb-4 lg:px-2"
            v-if="projectDimensions.north_wall_has_glass"
          >
            <label
              for="north_wall_glass_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="north_wall_glass_m2"
                v-model.trim="$v.projectDimensions.north_wall_glass_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.north_wall_glass_m2.$error,
                }"
              />
              <div
                v-if="$v.projectDimensions.north_wall_glass_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="w-full lg:w-1/2 mb-4 lg:px-2"
            v-if="projectDimensions.north_wall_has_doors"
          >
            <label
              for="north_wall_doors_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Puertas (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="north_wall_doors_m2"
                v-model.trim="$v.projectDimensions.north_wall_doors_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.north_wall_doors_m2.$error,
                }"
              />
              <div
                v-if="$v.projectDimensions.north_wall_doors_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <!-- North wall -->

        <!-- South wall -->
        <div class="w-full border-t border-gray-200 my-6"></div>
        <div class="w-full mb-4">
          <p class="text-gray-500 font-medium pb-2">
            Muro sur
          </p>
          <p class="text-gray-400 text-sm">
            Ingresa aquí la información de los metros cuadrados del muro sur.
          </p>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio</label
            >
            <div
              v-if="$v.projectDimensions.south_wall_has_glass.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.south_wall_has_glass.$model"
            :options="glassOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Puertas</label
            >
            <div
              v-if="$v.projectDimensions.south_wall_has_doors.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.south_wall_has_doors.$model"
            :options="doorsOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="south_wall_solid_m2"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Area de muro (m²)</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="south_wall_solid_m2"
              v-model.trim="$v.projectDimensions.south_wall_solid_m2.$model"
              type="number"
              step="any"
              required
              class="form-input block w-full sm:text-sm sm:leading-5"
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectDimensions.south_wall_solid_m2.$error,
              }"
            />
            <div
              v-if="$v.projectDimensions.south_wall_solid_m2.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="w-full lg:w-1/2 mb-4 lg:px-2"
            v-if="projectDimensions.south_wall_has_glass"
          >
            <label
              for="south_wall_glass_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="south_wall_glass_m2"
                v-model.trim="$v.projectDimensions.south_wall_glass_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.south_wall_glass_m2.$error,
                }"
              />
              <div
                v-if="$v.projectDimensions.south_wall_glass_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="w-full lg:w-1/2 mb-4 lg:px-2"
            v-if="projectDimensions.south_wall_has_doors"
          >
            <label
              for="south_wall_doors_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Puertas (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="south_wall_doors_m2"
                v-model.trim="$v.projectDimensions.south_wall_doors_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.south_wall_doors_m2.$error,
                }"
              />
              <div
                v-if="$v.projectDimensions.south_wall_doors_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <!-- South wall -->

        <!-- East wall -->
        <div class="w-full border-t border-gray-200 my-6"></div>
        <div class="w-full mb-4">
          <p class="text-gray-500 font-medium pb-2">
            Muro este
          </p>
          <p class="text-gray-400 text-sm">
            Ingresa aquí la información de los metros cuadrados del muro este.
          </p>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio</label
            >
            <div
              v-if="$v.projectDimensions.east_wall_has_glass.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.east_wall_has_glass.$model"
            :options="glassOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Puertas</label
            >
            <div
              v-if="$v.projectDimensions.east_wall_has_doors.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.east_wall_has_doors.$model"
            :options="doorsOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="east_wall_solid_m2"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Area de muro (m²)</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="east_wall_solid_m2"
              v-model.trim="$v.projectDimensions.east_wall_solid_m2.$model"
              type="number"
              step="any"
              required
              class="form-input block w-full sm:text-sm sm:leading-5"
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectDimensions.east_wall_solid_m2.$error,
              }"
            />
            <div
              v-if="$v.projectDimensions.east_wall_solid_m2.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="w-full lg:w-1/2 mb-4 lg:px-2"
            v-if="projectDimensions.east_wall_has_glass"
          >
            <label
              for="east_wall_glass_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="east_wall_glass_m2"
                v-model.trim="$v.projectDimensions.east_wall_glass_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.east_wall_glass_m2.$error,
                }"
              />
              <div
                v-if="$v.projectDimensions.east_wall_glass_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="w-full lg:w-1/2 mb-4 lg:px-2"
            v-if="projectDimensions.east_wall_has_doors"
          >
            <label
              for="east_wall_doors_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Puertas (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="east_wall_doors_m2"
                v-model.trim="$v.projectDimensions.east_wall_doors_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.east_wall_doors_m2.$error,
                }"
              />
              <div
                v-if="$v.projectDimensions.east_wall_doors_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <!-- East wall -->

        <!-- West wall -->
        <div class="w-full border-t border-gray-200 my-6"></div>
        <div class="w-full mb-4">
          <p class="text-gray-500 font-medium pb-2">
            Muro oeste
          </p>
          <p class="text-gray-400 text-sm">
            Ingresa aquí la información de los metros cuadrados del muro oeste.
          </p>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio</label
            >
            <div
              v-if="$v.projectDimensions.west_wall_has_glass.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.west_wall_has_glass.$model"
            :options="glassOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <div class="flex">
            <label class="block text-sm font-medium leading-5 text-gray-700"
              >Puertas</label
            >
            <div
              v-if="$v.projectDimensions.west_wall_has_doors.$error"
              class="pointer-events-none pl-2"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <select-buttons
            class="mt-1"
            v-model="$v.projectDimensions.west_wall_has_doors.$model"
            :options="doorsOptions"
          ></select-buttons>
        </div>

        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="west_wall_solid_m2"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Area de muro (m²)</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="west_wall_solid_m2"
              v-model.trim="$v.projectDimensions.west_wall_solid_m2.$model"
              type="number"
              step="any"
              required
              class="form-input block w-full sm:text-sm sm:leading-5"
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectDimensions.west_wall_solid_m2.$error,
              }"
            />
            <div
              v-if="$v.projectDimensions.west_wall_solid_m2.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="w-full lg:w-1/2 mb-4 lg:px-2"
            v-if="projectDimensions.west_wall_has_glass"
          >
            <label
              for="west_wall_glass_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Vidrio (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="west_wall_glass_m2"
                v-model.trim="$v.projectDimensions.west_wall_glass_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.west_wall_glass_m2.$error,
                }"
              />
              <div
                v-if="$v.projectDimensions.west_wall_glass_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <transition
          enter-active-class="ease-out duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="ease-in duration-200"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            class="w-full lg:w-1/2 mb-4 lg:px-2"
            v-if="projectDimensions.west_wall_has_doors"
          >
            <label
              for="west_wall_doors_m2"
              class="block text-sm font-medium leading-5 text-gray-700"
              >Puertas (m²)</label
            >
            <div class="mt-1 relative rounded-md shadow-sm">
              <input
                id="west_wall_doors_m2"
                v-model.trim="$v.projectDimensions.west_wall_doors_m2.$model"
                type="number"
                step="any"
                required
                class="form-input block w-full sm:text-sm sm:leading-5"
                :class="{
                  'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                    $v.projectDimensions.west_wall_doors_m2.$error,
                }"
              />
              <div
                v-if="$v.projectDimensions.west_wall_doors_m2.$error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition>
        <!-- West wall -->
      </div>
    </div>
    <action-modal
      @accepted="rotateClockwise"
      ref="rotateClockwiseModal"
      type="info"
      title="¿Deseas rotar las dimensiones de tu proyecto segun las manecillas de reloj?"
      text="Esto intercambiará los valores de la siguiente manera:"
      action-text="Rotar"
    >
      <div class="text-sm leading-5 text-gray-500 mt-2">
        <p>Norte => Este</p>
        <p>Este => Sur</p>
        <p>Sur => Oeste</p>
        <p>Oeste => Norte</p>
      </div>
    </action-modal>
    <action-modal
      @accepted="rotateCounterClockwise"
      ref="rotateCounterClockwiseModal"
      type="info"
      title="¿Deseas rotar las dimensiones de tu proyecto encontra de las manecillas de reloj?"
      text="Esto intercambiará los valores de la siguiente manera:"
      action-text="Rotar"
    >
      <div class="text-sm leading-5 text-gray-500 mt-2">
        <p>Norte => Oeste</p>
        <p>Oeste => Sur</p>
        <p>Sur => Este</p>
        <p>Este => Norte</p>
      </div>
    </action-modal>
  </div>
</template>

<script>
import { required, decimal, minValue } from 'vuelidate/lib/validators';

function boolean(value) {
  return typeof value === 'boolean';
}

function inArray(array) {
  return function(value) {
    return array.includes(value);
  };
}
export default {
  props: {
    projectDimensions: { type: Object }
  },
  methods: {
    rotateClockwise() {
      const form = {
        north_wall_has_glass: this.projectDimensions.north_wall_has_glass,
        north_wall_has_doors: this.projectDimensions.north_wall_has_doors,
        north_wall_glass_m2: this.projectDimensions.north_wall_glass_m2,
        north_wall_solid_m2: this.projectDimensions.north_wall_solid_m2,
        north_wall_doors_m2: this.projectDimensions.north_wall_doors_m2,
        south_wall_has_glass: this.projectDimensions.south_wall_has_glass,
        south_wall_has_doors: this.projectDimensions.south_wall_has_doors,
        south_wall_glass_m2: this.projectDimensions.south_wall_glass_m2,
        south_wall_solid_m2: this.projectDimensions.south_wall_solid_m2,
        south_wall_doors_m2: this.projectDimensions.south_wall_doors_m2,
        east_wall_has_glass: this.projectDimensions.east_wall_has_glass,
        east_wall_has_doors: this.projectDimensions.east_wall_has_doors,
        east_wall_glass_m2: this.projectDimensions.east_wall_glass_m2,
        east_wall_solid_m2: this.projectDimensions.east_wall_solid_m2,
        east_wall_doors_m2: this.projectDimensions.east_wall_doors_m2,
        west_wall_has_glass: this.projectDimensions.west_wall_has_glass,
        west_wall_has_doors: this.projectDimensions.west_wall_has_doors,
        west_wall_glass_m2: this.projectDimensions.west_wall_glass_m2,
        west_wall_solid_m2: this.projectDimensions.west_wall_solid_m2,
        west_wall_doors_m2: this.projectDimensions.west_wall_doors_m2,
      };
      this.projectDimensions.north_wall_has_glass = form.west_wall_has_glass;
      this.projectDimensions.north_wall_has_doors = form.west_wall_has_doors;
      this.projectDimensions.north_wall_glass_m2 = form.west_wall_glass_m2;
      this.projectDimensions.north_wall_solid_m2 = form.west_wall_solid_m2;
      this.projectDimensions.north_wall_doors_m2 = form.west_wall_doors_m2;
      this.projectDimensions.south_wall_has_glass = form.east_wall_has_glass;
      this.projectDimensions.south_wall_has_doors = form.east_wall_has_doors;
      this.projectDimensions.south_wall_glass_m2 = form.east_wall_glass_m2;
      this.projectDimensions.south_wall_solid_m2 = form.east_wall_solid_m2;
      this.projectDimensions.south_wall_doors_m2 = form.east_wall_doors_m2;
      this.projectDimensions.east_wall_has_glass = form.north_wall_has_glass;
      this.projectDimensions.east_wall_has_doors = form.north_wall_has_doors;
      this.projectDimensions.east_wall_glass_m2 = form.north_wall_glass_m2;
      this.projectDimensions.east_wall_solid_m2 = form.north_wall_solid_m2;
      this.projectDimensions.east_wall_doors_m2 = form.north_wall_doors_m2;
      this.projectDimensions.west_wall_has_glass = form.south_wall_has_glass;
      this.projectDimensions.west_wall_has_doors = form.south_wall_has_doors;
      this.projectDimensions.west_wall_glass_m2 = form.south_wall_glass_m2;
      this.projectDimensions.west_wall_solid_m2 = form.south_wall_solid_m2;
      this.projectDimensions.west_wall_doors_m2 = form.south_wall_doors_m2;
    },
    rotateCounterClockwise() {
      const form = {
        north_wall_has_glass: this.projectDimensions.north_wall_has_glass,
        north_wall_has_doors: this.projectDimensions.north_wall_has_doors,
        north_wall_glass_m2: this.projectDimensions.north_wall_glass_m2,
        north_wall_solid_m2: this.projectDimensions.north_wall_solid_m2,
        north_wall_doors_m2: this.projectDimensions.north_wall_doors_m2,
        south_wall_has_glass: this.projectDimensions.south_wall_has_glass,
        south_wall_has_doors: this.projectDimensions.south_wall_has_doors,
        south_wall_glass_m2: this.projectDimensions.south_wall_glass_m2,
        south_wall_solid_m2: this.projectDimensions.south_wall_solid_m2,
        south_wall_doors_m2: this.projectDimensions.south_wall_doors_m2,
        east_wall_has_glass: this.projectDimensions.east_wall_has_glass,
        east_wall_has_doors: this.projectDimensions.east_wall_has_doors,
        east_wall_glass_m2: this.projectDimensions.east_wall_glass_m2,
        east_wall_solid_m2: this.projectDimensions.east_wall_solid_m2,
        east_wall_doors_m2: this.projectDimensions.east_wall_doors_m2,
        west_wall_has_glass: this.projectDimensions.west_wall_has_glass,
        west_wall_has_doors: this.projectDimensions.west_wall_has_doors,
        west_wall_glass_m2: this.projectDimensions.west_wall_glass_m2,
        west_wall_solid_m2: this.projectDimensions.west_wall_solid_m2,
        west_wall_doors_m2: this.projectDimensions.west_wall_doors_m2,
      };
      this.projectDimensions.north_wall_has_glass = form.east_wall_has_glass;
      this.projectDimensions.north_wall_has_doors = form.east_wall_has_doors;
      this.projectDimensions.north_wall_glass_m2 = form.east_wall_glass_m2;
      this.projectDimensions.north_wall_solid_m2 = form.east_wall_solid_m2;
      this.projectDimensions.north_wall_doors_m2 = form.east_wall_doors_m2;
      this.projectDimensions.south_wall_has_glass = form.west_wall_has_glass;
      this.projectDimensions.south_wall_has_doors = form.west_wall_has_doors;
      this.projectDimensions.south_wall_glass_m2 = form.west_wall_glass_m2;
      this.projectDimensions.south_wall_solid_m2 = form.west_wall_solid_m2;
      this.projectDimensions.south_wall_doors_m2 = form.west_wall_doors_m2;
      this.projectDimensions.east_wall_has_glass = form.south_wall_has_glass;
      this.projectDimensions.east_wall_has_doors = form.south_wall_has_doors;
      this.projectDimensions.east_wall_glass_m2 = form.south_wall_glass_m2;
      this.projectDimensions.east_wall_solid_m2 = form.south_wall_solid_m2;
      this.projectDimensions.east_wall_doors_m2 = form.south_wall_doors_m2;
      this.projectDimensions.west_wall_has_glass = form.north_wall_has_glass;
      this.projectDimensions.west_wall_has_doors = form.north_wall_has_doors;
      this.projectDimensions.west_wall_glass_m2 = form.north_wall_glass_m2;
      this.projectDimensions.west_wall_solid_m2 = form.north_wall_solid_m2;
      this.projectDimensions.west_wall_doors_m2 = form.north_wall_doors_m2;
    },
  },
  data() {
    return {
      projectTypeOptions: [
        { key: 'residential', name: 'Residencial' },
        { key: 'commercial', name: 'Comercio' },
      ],
      orientationOptions: [
        { key: 'north', name: 'Norte' },
        { key: 'south', name: 'Sur' },
        { key: 'east', name: 'Este' },
        { key: 'west', name: 'Oeste' },
      ],
      glassOptions: [
        { key: false, name: 'No tiene vidrio' },
        { key: true, name: 'Tiene vidrio' },
      ],
      doorsOptions: [
        { key: false, name: 'No tiene puertas' },
        { key: true, name: 'Tiene puertas' },
      ],
    };
  },
  validations() {
    return {
      projectDimensions: {
        project_type: {
          required,
          inProductTypesArray: inArray([
            ...new Set(this.projectTypeOptions.map((a) => a.key)),
          ]),
        },
        orientation_front: {
          required,
          inOrientationArray: inArray([
            ...new Set(this.orientationOptions.map((a) => a.key)),
          ]),
        },
        roof_has_glass: {
          required,
          boolean,
        },
        roof_glass_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        roof_solid_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        north_wall_has_glass: {
          required,
          boolean,
        },
        north_wall_has_doors: {
          required,
          boolean,
        },
        north_wall_glass_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        north_wall_solid_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        north_wall_doors_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        south_wall_has_glass: {
          required,
          boolean,
        },
        south_wall_has_doors: {
          required,
          boolean,
        },
        south_wall_glass_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        south_wall_solid_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        south_wall_doors_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        east_wall_has_glass: {
          required,
          boolean,
        },
        east_wall_has_doors: {
          required,
          boolean,
        },
        east_wall_glass_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        east_wall_solid_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        east_wall_doors_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        west_wall_has_glass: {
          required,
          boolean,
        },
        west_wall_has_doors: {
          required,
          boolean,
        },
        west_wall_glass_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        west_wall_solid_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
        west_wall_doors_m2: {
          required,
          decimal,
          minValue: minValue(0),
        },
      },
    };
  },
  watch: {
    projectDimensions: {
      deep: true,

      handler() {
        if (!this.projectDimensions.roof_has_glass) {
          this.projectDimensions.roof_glass_m2 = 0;
        }
        if (!this.projectDimensions.north_wall_has_glass) {
          this.projectDimensions.north_wall_glass_m2 = 0;
        }
        if (!this.projectDimensions.north_wall_has_doors) {
          this.projectDimensions.north_wall_doors_m2 = 0;
        }
        if (!this.projectDimensions.south_wall_has_glass) {
          this.projectDimensions.south_wall_glass_m2 = 0;
        }
        if (!this.projectDimensions.south_wall_has_doors) {
          this.projectDimensions.south_wall_doors_m2 = 0;
        }
        if (!this.projectDimensions.east_wall_has_glass) {
          this.projectDimensions.east_wall_glass_m2 = 0;
        }
        if (!this.projectDimensions.east_wall_has_doors) {
          this.projectDimensions.east_wall_doors_m2 = 0;
        }
        if (!this.projectDimensions.west_wall_has_glass) {
          this.projectDimensions.west_wall_glass_m2 = 0;
        }
        if (!this.projectDimensions.west_wall_has_doors) {
          this.projectDimensions.west_wall_doors_m2 = 0;
        }

        this.$emit('project-dimensions-changed', this.projectDimensions);
      },
    },
  },
};
</script>
